import { useState } from "react";

// Storage service API key for M2M authentication
const STORAGE_SERVICE_API_KEY = process.env.REACT_APP_STORAGE_SERVICE_API_KEY;

/**
 * Interface for the result object returned by useFetch
 */
interface FetchResult {
  connecting: boolean;
  response: string | null;
  error: string | null;
  handleClick: (evt?: React.MouseEvent) => void;
  fetchData: () => void;
}

/**
 * Custom hook for making fetch requests
 * 
 * @param url - The URL to fetch from
 * @param payload - The data to send in the request body (used for POST requests)
 * @param httpMethod - The HTTP method to use ("GET" or "POST")
 * @returns Object containing state and handlers for the fetch operation
 */
const useFetch = (url: string, payload: Record<string, any> = {}, httpMethod = "GET"): FetchResult => {
  // State to track the connection status
  const [connecting, setConnecting] = useState<boolean>(false);
  // State to store the response
  const [response, setResponse] = useState<string | null>(null);
  // State to store any errors
  const [error, setError] = useState<string | null>(null);

  // Reset state when URL changes (for retry scenarios)
  const [previousUrl, setPreviousUrl] = useState<string>(url);
  if (url !== previousUrl) {
    setPreviousUrl(url);
    setResponse(null);
    setError(null);
    setConnecting(false);
  }

  /**
   * Handle click event and initiate fetch
   * 
   * @param evt - Optional mouse event
   */
  const handleClick = (evt?: React.MouseEvent): void => {
    setConnecting(true);
    setResponse(null);
    setError(null);

    /**
     * Asynchronous function to perform the fetch operation
     * 
     * @param fetchPayload - The payload to send with the request
     * @param httpMethod - The HTTP method to use
     */
    const performFetch = async (fetchPayload: Record<string, any>, httpMethod: string): Promise<void> => {
      try {
        const fetchOptions: RequestInit = {
          method: httpMethod,
          headers: {
            "accept": "*/*",
            ...(STORAGE_SERVICE_API_KEY && { "Authorization": `Bearer ${STORAGE_SERVICE_API_KEY}` })
          }
        };

        // Add payload and content-type for POST requests
        if (httpMethod === "POST") {
          fetchOptions.headers = {
            ...fetchOptions.headers,
            "content-type": "text/plain"
          };
          fetchOptions.body = JSON.stringify(fetchPayload);
        } else {
          // For GET requests, add access-control-allow-origin header
          fetchOptions.headers = {
            ...fetchOptions.headers,
            "access-control-allow-origin": "*"
          };
        }

        console.log(`useFetch: Making ${httpMethod} request to: ${url}`);
        const response = await fetch(url, fetchOptions);

        if (response.ok) {
          const content = await response.text();
          console.log(`useFetch: Successfully received response from ${url}`);
          const data = JSON.parse(content);
          data.success = true; // Ensure the data has a success property
          data.latestrevision = true;
          setResponse(JSON.stringify(data));
        } else {
          const errorText = await response.text();
          console.error(`useFetch: HTTP ${response.status} error from ${url}:`, errorText);
          const errorMessage = response.status === 404 
            ? `Design not found (HTTP 404). The design may have been deleted or moved. URL: ${url}`
            : `Server error (${response.status}): ${errorText}. URL: ${url}`;
          setError(errorMessage);
        }
      } catch (err) {
        // Handle any fetch errors
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setConnecting(false);
      }
    };

    // Validate httpMethod parameter
    const normalizedMethod = httpMethod.toUpperCase();
    if (!["GET", "POST"].includes(normalizedMethod)) {
      setError(`Unsupported HTTP method: ${httpMethod}`);
      setConnecting(false);
      return;
    }

    performFetch(payload, normalizedMethod);
  };

  /**
   * Convenience method to fetch data without an event
   */
  const fetchData = (): void => handleClick();

  return { connecting, response, error, handleClick, fetchData };
};

export default useFetch;
